import { Suspense, lazy } from 'react';

import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, HStack, Heading, Icon, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import sdiIntroductionAnimation from './sdi_introduction.lottie?url';

import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';

const DotLottieReact = lazy(() =>
  import('@lottiefiles/dotlottie-react').then(module => ({
    default: module.DotLottieReact,
  }))
);

export const IntroductionStep: React.FC<StepProps> = ({ goToNextStep, flowState }) => {
  const navigate = useAutoNavigate();

  return (
    <WorkflowLayout
      feature="add_direct_trading_account"
      pageTitle="Introduction"
      workflowTitle=""
      onClose={() => navigate(flowState.returnToPath)}
      size="full"
    >
      <Box
        css={{
          display: 'flex',
          gap: '56px',
          smDown: {
            display: 'grid',
            gap: 'space.200',
          },
        }}
      >
        <VStack css={{ width: '50%', smDown: { width: '100%' } }}>
          <Box
            css={css.raw({
              w: '100%',
              background: 'linear-gradient(#000B50, #1D6AE5)',
              borderRadius: '22px',
              display: 'flex',
              flexDir: 'row',
              alignItems: 'center',
              aspectRatio: '1/1',
            })}
          >
            <Suspense fallback={<Box css={{ width: '100%', height: '100%' }} />}>
              <DotLottieReact src={sdiIntroductionAnimation} autoplay={true} loop={false} />
            </Suspense>
          </Box>
          <Text variant="text25" color="text.weak">
            Logos above are trademarks of their respective companies.
          </Text>
        </VStack>
        <VStack css={{ width: '50%', gap: 'space.500', smDown: { width: '100%' } }}>
          <Box>
            <Heading css={{ lgDown: { fontSize: 'fontSize.300' } }}>Self-directed investing</Heading>
            <Text>Choose from thousands of stocks and ETFs with Betterment by your side.</Text>
          </Box>
          <VStack css={{ gap: 'space.300' }}>
            <HStack css={{ gap: 'space.150' }}>
              <Icon name="Donut" size="small" color="icon.selected" css={{ flexShrink: '0' }} />
              <VStack>
                <Heading variant="title100">Invest the way you want</Heading>
                <Text color="text.weak" variant="text100">
                  Buy and sell individual securities alongside your managed accounts. Get more control and tax
                  transparency with all your investments in one place.
                </Text>
              </VStack>
            </HStack>
            <HStack css={{ gap: 'space.150' }}>
              <Icon name="Calculator" size="small" color="icon.selected" css={{ flexShrink: '0' }} />
              <VStack>
                <Heading variant="title100">See tax impact before you sell</Heading>
                <Text color="text.weak" variant="text100">
                  Unlike other investing apps, we’ll preview how your trade could affect your taxes, so you can make
                  informed decisions with no surprises.
                </Text>
              </VStack>
            </HStack>
            <HStack css={{ gap: 'space.150' }}>
              <Icon name="PieAlt" size="small" color="icon.selected" css={{ flexShrink: '0' }} />
              <VStack>
                <Heading variant="title100">Trade fractional shares</Heading>
                <Text color="text.weak" variant="text100">
                  Invest in stocks and ETFs without needing to buy a full share.
                </Text>
              </VStack>
            </HStack>
            <HStack css={{ gap: 'space.150' }}>
              <Icon name="DollarCircle" size="small" color="icon.selected" css={{ flexShrink: '0' }} />
              <VStack>
                <Heading variant="title100">Pay no commission or management fees</Heading>
                <Text color="text.weak" variant="text100">
                  You’ll pay no trading commissions, and your usual Betterment management fee doesn’t apply.
                </Text>
              </VStack>
            </HStack>
          </VStack>
          <Box
            css={{
              mt: 'space.100',
              smDown: {
                display: 'flex',
                position: 'fixed',
                bottom: 'space.0',
                left: 'space.0',
                right: 'space.0',
                justifyContent: 'center',
                backgroundColor: 'background.surface',
                paddingY: 'space.100',
                paddingX: 'space.300',
              },
            }}
          >
            <Button onClick={() => goToNextStep()} variant="solidPrimary" css={{ smDown: { width: '100%' } }}>
              Get started
            </Button>
          </Box>
        </VStack>
      </Box>
    </WorkflowLayout>
  );
};
